import { apiClient } from '@/lib/apiClient'
import { getVisitorIds } from '@/lib/visitor'
import type {
  OperatorApplicationPayload,
  OperatorProfile,
  OperatorProfileDraft,
  OnboardingStatusResponse,
  ReferralOverview,
} from '@/types/operator.types'
import type { ApiResponse } from '@/types/api.types'

/** Salvataggio incrementale dei dati fiscali. NON cambia onboardingStep. */
export async function saveFiscalData(
  payload: OperatorApplicationPayload,
): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/apply',
    payload,
  )
  return data.data
}

export interface GdprConsentPayload {
  privacyAccepted: true
  termsAccepted:   true
  privacyPolicyVersion?: string
  termsOfUseVersion?:    string
}

/** Step 2 onboarding: accetta informativa privacy + termini d'uso piattaforma.
 *  Transizione INVITED → CONSENT_GIVEN. Idempotente. */
export async function acceptGdpr(
  payload: GdprConsentPayload,
): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/me/accept-gdpr',
    { ...payload, ...getVisitorIds() },
  )
  return data.data
}

/** Invia la candidatura completa (dati fiscali + wizard) → APPLIED.
 *  Il backend verifica che tutti i requisiti minimi siano presenti. */
export async function submitApplicationFinal(): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/me/submit-application',
  )
  return data.data
}

/** @deprecated alias retrocompatibile per chi importa `submitApplication`.
 *  Si comporta come `saveFiscalData` (no transizione di stato). */
export const submitApplication = saveFiscalData

/** Submit wizard draft → status: PENDING_REVIEW */
export async function submitProfile(
  draft: Partial<OperatorProfileDraft>
): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/profile',
    draft
  )
  return data.data
}

/** Get current operator's own profile */
export async function getMyProfile(): Promise<OperatorProfile | null> {
  try {
    const { data } = await apiClient.get<ApiResponse<OperatorProfile>>('/operators/me')
    return data.data
  } catch (err: unknown) {
    const e = err as { response?: { status: number } }
    if (e.response?.status === 404) return null
    throw err
  }
}

/** Update existing profile (PATCH) */
export async function updateProfile(
  patch: Partial<OperatorProfileDraft>
): Promise<OperatorProfile> {
  const { data } = await apiClient.patch<ApiResponse<OperatorProfile>>(
    '/operators/me',
    patch
  )
  return data.data
}

export interface ReferralResult {
  email:     string
  sent:      boolean
  inviteUrl: string
}

/** "Invita un collega": invia un invito referral a un altro operatore.
 *  L'invio email è automatico; l'invito annota `referredBy` = operatore corrente. */
export async function sendReferralInvite(payload: {
  email:      string
  firstName?: string
  lastName?:  string
}): Promise<ReferralResult> {
  const { data } = await apiClient.post<ApiResponse<ReferralResult>>(
    '/operators/me/referrals',
    payload,
  )
  return data.data
}

/** «Invita un collega»: i miei inviti con stato e bonus maturati.
 *  Il backend applica prima (lazy) gli eventuali bonus rimasti in attesa. */
export async function getMyReferrals(): Promise<ReferralOverview> {
  const { data } = await apiClient.get<ApiResponse<ReferralOverview>>(
    '/operators/me/referrals',
  )
  return data.data
}

/** Accetta il contratto operatore (step 4 onboarding) → CONTRACT_ACCEPTED */
export async function acceptContract(
  contractVersion: string,
): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/me/accept-contract',
    { accepted: true, contractVersion },
  )
  return data.data
}

/** Aggiorna la sola `dateOfBirth` sul PROFILE — backfill per compliance DAC7.
 *  Funziona in qualunque stato (eccetto REJECTED): non passa dal flusso
 *  di moderazione admin né tocca il KYC Stripe.
 *  Dopo il save il backend ri-esegue `recompute_and_advance`, quindi un
 *  operatore prima retrocesso (PUBLISHED → SUBSCRIBED) può tornare PUBLISHED. */
export async function updateBirthDate(dateOfBirth: string): Promise<OperatorProfile> {
  const { data } = await apiClient.post<ApiResponse<OperatorProfile>>(
    '/operators/me/birth-date',
    { dateOfBirth },
  )
  return data.data
}

/** Ritorna la bacheca onboarding 7-step + gates + profilo (M5). */
export async function getOnboardingStatus(): Promise<OnboardingStatusResponse> {
  const { data } = await apiClient.get<ApiResponse<OnboardingStatusResponse>>(
    '/operators/me/onboarding-status',
  )
  return data.data
}

// ── Copertura disponibilità per luogo ────────────────────────────────────────

export interface AvailabilityCoverageItem {
  locationId:    string
  label:         string
  /** Minuti settimanali di disponibilità assegnati a questo luogo. */
  weeklyMinutes: number
  servicesCount: number
  /** NO_AVAILABILITY = ci sono servizi ma nessuna fascia; NO_SERVICES = il contrario. */
  status:        'OK' | 'NO_AVAILABILITY' | 'NO_SERVICES'
}

export interface AvailabilityCoverage {
  items:               AvailabilityCoverageItem[]
  /** Fasce ancora senza luogo assegnato. */
  unassignedWindows:   number
  travelBufferMinutes: number
}

/** Diagnostica non bloccante: dove l'operatore riceve, dove ha servizi e dove
 *  le due cose non combaciano. Alimenta i banner di dashboard/servizi/orari. */
export async function getAvailabilityCoverage(): Promise<AvailabilityCoverage> {
  const { data } = await apiClient.get<ApiResponse<AvailabilityCoverage>>(
    '/operators/me/availability-coverage',
  )
  return data.data
}

/** URL del feed iCalendar (sottoscrivibile in Google Calendar / Apple / Outlook).
 *  Il token viene creato al primo accesso. */
export async function getCalendarFeed(): Promise<{ feedUrl: string }> {
  const { data } = await apiClient.get<ApiResponse<{ feedUrl: string }>>(
    '/operators/me/calendar-feed',
  )
  return data.data
}

/** Rigenera il token del feed: il vecchio URL smette di funzionare. */
export async function regenerateCalendarFeed(): Promise<{ feedUrl: string }> {
  const { data } = await apiClient.post<ApiResponse<{ feedUrl: string }>>(
    '/operators/me/calendar-feed/regenerate',
    {},
  )
  return data.data
}
